import React, { useState } from 'react';
import { Plus, Users as UsersIcon, MoreVertical, Edit, UserCog, Trash2, KeyRound } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { EmptyState } from '../../components/shared/EmptyState';
import { DataTable } from '../../components/shared/DataTable';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/DropdownMenu';
import { useUsers } from '../../core/hooks/useUsers';
import { useAuth } from '../../core/contexts/AuthContext';
import { UserManagementDialog } from './components/UserManagementDialog';
import { ClientAccessDialog } from './components/ClientAccessDialog';
import { ResetPasswordDialog } from './components/ResetPasswordDialog';
import { UserRole } from '../../core/models/User';
import { ColumnDef } from '@tanstack/react-table';
import type { User } from '../../core/models/User';

export const UsersPage: React.FC = () => {
  const { organizationId, currentUser } = useAuth();
  const { users, loading, error, createUser, updateUser, updateUserClientAccess, setUserOrganizationClaims, updateUserRole, deleteUser, resetUserPassword } = useUsers(organizationId);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [managingClientAccessUser, setManagingClientAccessUser] = useState<User | null>(null);
  const [resettingPasswordUser, setResettingPasswordUser] = useState<User | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  const handleCreateUser = async (data: any) => {
    try {
      if (data.role !== UserRole.SUPER_ADMIN && !data.organizationId && !organizationId && currentUser?.role !== UserRole.SUPER_ADMIN) {
        alert('Client ID is required. Please ensure you are logged in with a valid client context.');
        return;
      }
      await createUser({
        ...data,
        organizationId: data.role === UserRole.SUPER_ADMIN ? '' : (data.organizationId || organizationId || ''),
      });
      setIsCreateDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to create user:', err);
      const errorMessage = err?.message || 'Failed to create user. Please try again.';
      alert(errorMessage);
    }
  };

  const handleUpdateUser = async (data: any) => {
    if (!editingUser) return;
    try {
      const normalizedUpdates = data.role === UserRole.SUPER_ADMIN
        ? { ...data, organizationId: null, clientId: null, projectAccess: {}, clientAccess: {} }
        : data;
      await updateUser(editingUser.uid, normalizedUpdates);
      if (data.role === UserRole.SUPER_ADMIN) {
        await updateUserRole(editingUser.uid, data.role);
      } else if (data.organizationId && data.role) {
        await setUserOrganizationClaims(editingUser.uid, data.organizationId, data.role);
      }
      setEditingUser(null);
    } catch (err: any) {
      console.error('Failed to update user:', err);
      const errorMessage = err?.message || 'Failed to update user. Please try again.';
      alert(errorMessage);
    }
  };

  const handleSaveClientAccess = async (clientAccess: Record<string, { role: string }>) => {
    if (!managingClientAccessUser) return;
    try {
      await updateUserClientAccess(managingClientAccessUser.uid, clientAccess);
      setManagingClientAccessUser(null);
    } catch (err: any) {
      console.error('Failed to update client access:', err);
      const errorMessage = err?.message || 'Failed to update project access. Please try again.';
      alert(errorMessage);
      throw err;
    }
  };

  const handleResetPassword = async (newPassword: string) => {
    if (!resettingPasswordUser) return;
    try {
      await resetUserPassword(resettingPasswordUser.uid, newPassword);
      setResettingPasswordUser(null);
      alert('Password has been reset successfully.');
    } catch (err: any) {
      console.error('Failed to reset password:', err);
      throw err;
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`)) {
      return;
    }

    setDeletingUserId(user.uid);
    try {
      await deleteUser(user.uid);
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      const errorMessage = err?.message || 'Failed to delete user. Please try again.';
      alert(errorMessage);
    } finally {
      setDeletingUserId(null);
    }
  };

  const getClientAccessCount = (user: User): number => {
    return user.clientAccess ? Object.keys(user.clientAccess).length : 0;
  };

  const getRoleColor = (role?: UserRole) => {
    switch (role) {
      case UserRole.SUPER_ADMIN:
        return 'error';
      case UserRole.CLIENT_ADMIN:
        return 'warning';
      case UserRole.PROJECT_ADMIN:
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role?: UserRole) => {
    switch (role) {
      case UserRole.SUPER_ADMIN:
        return 'Super Admin';
      case UserRole.CLIENT_ADMIN:
        return 'Client Admin';
      case UserRole.PROJECT_ADMIN:
        return 'Project Admin';
      default:
        return role || 'N/A';
    }
  };

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: 'displayName',
      header: 'Name',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">
            {row.original.displayName || 'N/A'}
          </div>
          <div className="text-sm text-muted-foreground">
            {row.original.email}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => row.original.email || 'N/A',
    },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) => (
        <Badge variant={getRoleColor(row.original.role)}>
          {getRoleLabel(row.original.role)}
        </Badge>
      ),
    },
    {
      accessorKey: 'companyRole',
      header: 'Company Role',
    },
    {
      accessorKey: 'phoneNumber',
      header: 'Phone',
    },
    {
      id: 'clientAccess',
      header: 'Project Access',
      cell: ({ row }) => {
        const count = getClientAccessCount(row.original);
        return <span className="text-sm text-gray-600">{count} {count === 1 ? 'project' : 'projects'}</span>;
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const user = row.original;
        const isDeleting = deletingUserId === user.uid;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" disabled={isDeleting}>
                {isDeleting ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                ) : (
                  <MoreVertical className="h-4 w-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditingUser(user)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit User
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setManagingClientAccessUser(user)}
                disabled={!user.organizationId}
              >
                <UserCog className="mr-2 h-4 w-4" />
                Manage Project Access
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setResettingPasswordUser(user)}>
                <KeyRound className="mr-2 h-4 w-4" />
                Reset Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDeleteUser(user)}
                disabled={isDeleting}
                className="text-red-600 focus:text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  if (loading) {
    return <LoadingState message="Loading users..." />;
  }

  if (error) {
    return <ErrorState message="Failed to load users" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="Manage user accounts and permissions"
        icon={UsersIcon}
        actions={
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        }
      />

      {users.length === 0 ? (
        <EmptyState
          icon={UsersIcon}
          title="No users found"
          description="Add your first user to get started"
          action={{
            label: 'Add User',
            onClick: () => setIsCreateDialogOpen(true),
          }}
        />
      ) : (
        <DataTable columns={columns} data={users} searchKey="email" searchPlaceholder="Search users..." />
      )}

      {/* Create User Dialog */}
      <UserManagementDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateUser}
      />

      {/* Edit User Dialog */}
      {editingUser && (
        <UserManagementDialog
          open={!!editingUser}
          onClose={() => setEditingUser(null)}
          user={editingUser}
          onSubmit={handleUpdateUser}
        />
      )}

      {/* Reset Password Dialog */}
      <ResetPasswordDialog
        open={!!resettingPasswordUser}
        onClose={() => setResettingPasswordUser(null)}
        user={resettingPasswordUser}
        onReset={handleResetPassword}
      />

      {/* Client Access Dialog */}
      {managingClientAccessUser && (
        <ClientAccessDialog
          open={!!managingClientAccessUser}
          onClose={() => setManagingClientAccessUser(null)}
          user={managingClientAccessUser}
          onSave={handleSaveClientAccess}
        />
      )}
    </div>
  );
};
